import React, { useEffect, useState } from "react";
import BreadCrumb from "src/Components/GlobalComponents/BreadCrumb";
import TableWrapper from "src/Components/GlobalComponents/TableWrapper";
import PharmaCategoryHeader from "src/Components/PharmacyComponents/Admin/PharmaCategoryHeader";
import httpRequest from "src/helpers/httpRequest";
import { IMainCategory, IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import { toast } from "react-toastify";
import emtyImg from "../../../assets/images/emty-img.png";
import editIcon from "../../../assets/images/edit-icon.png";
import deleteIcon from "../../../assets/images/delete-icon.png";
import ConfirmationModal from "src/Components/GlobalComponents/ConfirmationModal";
import UpsertMainCategory from "src/Components/PharmacyComponents/Admin/UpsertMainCategory";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite } from "src/helpers/helperFunctions";
import { updatePharmacyCategories } from "src/Store/realTimeState";
interface IResponce {
  documents: IMainCategory[];
  paginated: IPaginated;
  resultCount: {
    mainCategories: number;
    subCategories: number;
    childCategories: number;
  };
}

const PMainCategories: React.FC = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "pharmaInventory", false);
  const [isUpsert, setIsUpsert] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<IMainCategory | null>(null);

  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [displayData, setDisplayData] = useState<IMainCategory[]>([]);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    search: "",
    bool: false,
  });
  const { pharmacyCategories } = useSelector((store: RootState) => store.realTimeState);
  const dispatch = useDispatch();
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // state for handle pagination like a current page, page limit & total pages

  // Function to toggle between create and edit mode
  const handleClose = () => {
    setIsEdit(null);
    setIsUpsert((prev) => !prev);
  };

  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  // Function to set the component to edit mode and populate it with category data
  const handleEdit = (obj: IMainCategory) => {
    if (!hasWriteAccess) {
      return;
    }
    setIsUpsert(true);
    setIsEdit(obj);
  };

  // Function to show the delete confirmation modal and set the ID of the category to be deleted
  const handleDelete = (id: string) => {
    if (!hasWriteAccess) {
      return;
    }
    setDeleteModal(true);
    setDeleteId(id);
  };

  // Function to close the delete confirmation modal
  const deleteModalClose = () => {
    setDeleteModal(false);
    setDeleteId("");
  };

  // Function change search text for api query params
  const handleChangeText = (txt: string) => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search: txt,
    }));
  };

  // Function to update the list of categories after creating or editing a category
  const handleUpdateList = () => {
    setQueryParams((prev) => ({
      ...prev,
      bool: !prev.bool,
    }));
    setIsUpsert(false);
  };

  // API_CALL: Function to delete a category by its ID
  const deleteRequest = async () => {
    setDeleteLoader(true);
    const { res, err } = await httpRequest<any>({
      method: "delete",
      path: `/main-category/${deleteId}`,
      params: {},
    });
    if (res) {
      toast("Main category deleted successfully.", { type: "success" });
      setQueryParams((prev) => ({
        ...prev,
        bool: !prev.bool,
        page:
          displayData?.length > 1
            ? prev?.page
            : prev?.page > 1
            ? prev?.page - 1
            : prev?.page,
      }));
      setDeleteModal(false);
      setDeleteId("");
    } else {
      toast(err, { type: "error" });
    }
    setDeleteLoader(false);
  };

  // API_CALL: Function to fetch all main categories of pharmacy based on query parameters
  const fetch = async () => {
    setIsLoader(true);
    const { bool, ...remainingParams } = queryParams;
    const { res, err } = await httpRequest<IResponce>({
      path: `/main-category/getAll?${qs.stringify(remainingParams)}`,
    });
    if (res) {
      const {
        documents = [],
        resultCount = {
          mainCategories: 0,
          subCategories: 0,
          childCategories: 0,
        },
        paginated = {
          currentPage: 1,
          totalPages: 1,
        },
      } = res;
      setDisplayData(documents);
      setPaginated(paginated);
      dispatch(updatePharmacyCategories(resultCount));
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    fetch();
  }, [queryParams]);

  return (
    <>
      <ConfirmationModal
        show={deleteModal}
        onClose={deleteModalClose}
        title="Are you sure you want to delete?"
        leftBtnText="No"
        rightBtnText="Yes"
        onConfirm={deleteRequest}
        loader={deleteLoader}
      />
      <BreadCrumb
        current="Pharma Categories"
        previous="dashboard"
        navigateUrl="/admin"
        btnText={"Add Main Category"}
        btnShow={true}
        onPress={() => {
          if (!hasWriteAccess) {
            return;
          }
          handleClose();
        }}
        isUpsert={isUpsert}
        btnClassName={`${!hasWriteAccess ? "cursor-not-allowed" : ""}`}
        BtnId="add-category-btn"
      />
      {isUpsert ? (
        <UpsertMainCategory onSuccess={handleUpdateList} isEdit={isEdit} />
      ) : (
        <div className="table-container">
          <PharmaCategoryHeader searchApply={handleChangeText} counts={pharmacyCategories} />
          <TableWrapper
            isLoading={isLoader}
            thead={["S.No", "Category Name", "Category Image", "Action"]}
            page={paginated?.currentPage || 1}
            total={paginated?.totalPages || 0}
            columnWidth="15% 35% 35% 15%"
            onChange={handlePageChange}
          >
            <div className="tbody-content-height">
              {displayData?.map((item: IMainCategory, index: number) => (
                <ul
                  key={index}
                  className="font-size-16px tbody-light-table font-Poppins-Regular grid"
                  style={{ gridTemplateColumns: "15% 35% 35% 15%" }}
                >
                  <li>
                    {(paginated?.currentPage - 1) * queryParams.limit +
                      (index + 1)}
                  </li>
                  <li>{item?.categoryName || ""}</li>
                  <li className="action-last-colum-img">
                    <img
                      src={`${item?.categoryImage}`}
                      alt="category"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement; // Type assertion
                        target.src = emtyImg; // Handle error by setting default image
                      }}
                    />
                  </li>
                  <li className="action-last-colum">
                    <img
                      id={`edit-category-btn-${index + 1}`}
                      onClick={() => handleEdit(item)}
                      src={editIcon}
                      alt=""
                      className={`${!hasWriteAccess ? "read_access" : ""}`}
                    />
                    <img
                      id={`delete-category-btn-${index + 1}`}
                      onClick={() => handleDelete(item?._id)}
                      src={deleteIcon}
                      alt=""
                      className={`${!hasWriteAccess ? "read_access" : ""}`}
                    />
                  </li>
                </ul>
              ))}
            </div>
          </TableWrapper>
        </div>
      )}
    </>
  );
};

export default PMainCategories;
